import { DATE_FORMAT } from '@/constants/common.constant'
import { AxiomReportType } from '@/enums/axiom-report.enum'
import { fetchReportFiltersAsync } from '@/services/axiomReport.service'
import { setSelectedReport } from '@/states/axiomReport.slice'
import { Loader } from '@components/Base/Loader/Loader'
import { useAppDispatch } from '@hooks/use-app-dispatch'
import { IReportDTO } from '@interfaces/report.interface'
import { Close } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

export type IReportFilter = {
  selectedReport: IReportDTO
}

export const ReportFilter: React.FC<IReportFilter> = ({ selectedReport }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [isApplyChangeEnabled, setIsApplyChangeEnabled] = useState(false)
  const [initialValue, setInitialValue] = useState({
    portfolio: '',
    benchmark: '',
    template: '',
    date: '',
  })
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [portfolioList, setPortfolioList] = useState<string[]>([])
  const [benchmarkList, setBenchmarkList] = useState<string[]>([])
  const [templateList, setTemplateList] = useState<string[]>([])
  const [dateList, setDateList] = useState<string[]>([])
  const [filterReport, setFilterReport] = useState<string[]>([])
  const [portfolio, setPortfolio] = useState<string>('')
  const [benchmark, setBenchmark] = useState<string>('')
  const [template, setTemplate] = useState<string>('')
  const [date, setDate] = useState<string>('')
  const [matrix, setMatrix] = useState<string>('')
  const [initialLoad, setInitialLoad] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    setPortfolioList([selectedReport.portfolioName])
    initialFormValues(selectedReport)
    const selectedDate =
      selectedReport.reportType === AxiomReportType.GFR ||
      selectedReport.reportType === AxiomReportType.SCEN
        ? `${dayjs(selectedReport.beginDate)?.format(DATE_FORMAT.TABLE_CHART)}`
        : `${dayjs(selectedReport.beginDate)?.format(DATE_FORMAT.TABLE_CHART)} - ${dayjs(selectedReport.endDate)?.format(DATE_FORMAT.TABLE_CHART)}`
    setInitialValue({
      portfolio: selectedReport.portfolioName,
      benchmark: selectedReport.benchmarkName,
      template: selectedReport.templateName,
      date: selectedDate,
    })
  }, [selectedReport])

  const initialFormValues = (selectedReportItem: IReportDTO) => {
    const selectedDate =
      selectedReportItem.reportType === AxiomReportType.GFR ||
      selectedReportItem.reportType === AxiomReportType.SCEN
        ? `${dayjs(selectedReportItem.beginDate)?.format(DATE_FORMAT.TABLE_CHART)}`
        : `${dayjs(selectedReportItem.beginDate)?.format(DATE_FORMAT.TABLE_CHART)} - ${dayjs(selectedReportItem.endDate)?.format(DATE_FORMAT.TABLE_CHART)}`
    setBenchmarkList([selectedReportItem.benchmarkName])
    setTemplateList([selectedReportItem.templateName])
    setDateList([selectedDate])
    setPortfolio(selectedReportItem.portfolioName)
    setBenchmark(selectedReportItem.benchmarkName)
    setTemplate(selectedReportItem.templateName)
    setDate(selectedDate)
    setInitialLoad(true)
  }

  useEffect(() => {
    const fetchReportFilters = async () => {
      try {
        setLoading(true)
        const reportType = selectedReport.reportType
        if (reportType && !portfolio && !benchmark && !template && !date) {
          setIsApplyChangeEnabled(false)
          const response = await fetchReportFiltersAsync(reportType)
          setPortfolioList(response.portfolioName)
          if (response.portfolioName.length === 1) {
            setPortfolio(response.portfolioName[0])
          }
        } else if (
          reportType &&
          portfolio &&
          !benchmark &&
          !template &&
          !date
        ) {
          setIsApplyChangeEnabled(false)
          const response = await fetchReportFiltersAsync(reportType, portfolio)
          setBenchmarkList(response.benchmarkName)
          if (response.benchmarkName.length === 1) {
            setBenchmark(response.benchmarkName[0])
          }
        } else if (reportType && portfolio && benchmark && !template && !date) {
          setIsApplyChangeEnabled(false)
          const response = await fetchReportFiltersAsync(
            reportType,
            portfolio,
            benchmark,
          )
          setTemplateList(response.templateName)
          if (response.templateName.length === 1) {
            setTemplate(response.templateName[0])
          }
        } else if (reportType && portfolio && benchmark && template && !date) {
          setIsApplyChangeEnabled(false)
          const response = await fetchReportFiltersAsync(
            reportType,
            portfolio,
            benchmark,
            template,
          )
          setDateList(response.dateRange)
          if (response.dateRange.length === 1) {
            setDate(response.dateRange[0])
          }
        } else if (reportType && portfolio && benchmark && template && date) {
          !initialLoad && setIsApplyChangeEnabled(true)
          if (initialLoad) {
            setIsApplyChangeEnabled(false)
            const response = await fetchReportFiltersAsync(reportType)
            setPortfolioList(response.portfolioName)
            if (response.portfolioName.length === 1) {
              setPortfolio(response.portfolioName[0])
            }
          }
        }
      } catch (err) {
        console.warn(err)
      } finally {
        setLoading(false)
      }
    }

    fetchReportFilters()
  }, [selectedReport, portfolio, benchmark, template, date, initialLoad])

  const handleChangePortfolio = (event, value) => {
    setPortfolio(value)
    setBenchmarkList([])
    setTemplateList([])
    setDateList([])
    setBenchmark('')
    setTemplate('')
    setDate('')
    setInitialLoad(false)
  }

  const handleChangeBenchmark = async (event, value) => {
    setBenchmark(value)
    setTemplateList([])
    setDateList([])
    setTemplate('')
    setDate('')
    setInitialLoad(false)
  }

  const handleChangeTemplate = async (event, value) => {
    setTemplate(value)
    setDateList([])
    setDate('')
    setInitialLoad(false)
  }

  const handleChangeDate = async (event, value) => {
    setInitialLoad(false)
    setDate(value)
  }

  const handleApplyChange = async () => {
    setEditModalOpen(false)
    try {
      const response = await fetchReportFiltersAsync(
        selectedReport.reportType,
        portfolio,
        benchmark,
        template,
        date,
      )
      setFilterReport(response.reportId)
      response.matrixName.length && setMatrix(response.matrixName[0])
      setInitialValue({ portfolio, benchmark, template, date })
      setInitialLoad(true)
      const locationArr = location.pathname.split('/')
      const reportType = locationArr[locationArr.length - 2]
      const activeTab = locationArr[locationArr.length - 1]
      navigate(
        `/reports/${parseInt(response.reportId[0])}/${reportType}/${activeTab}`,
      )
    } catch (err) {
      console.warn(err)
    }
  }

  useEffect(() => {
    if (filterReport.length) {
      const updateSelectedReport = {
        reportId: parseInt(filterReport[0]),
        reportType: selectedReport.reportType,
        beginDate: date.split('-')[0],
        endDate:
          selectedReport.reportType === AxiomReportType.GFR ||
          selectedReport.reportType === AxiomReportType.SCEN
            ? date.split('-')[0]
            : date.split('-')[1],
        portfolioName: portfolio,
        benchmarkName: benchmark,
        templateName: template,
        matrixName: matrix,
      }
      dispatch(setSelectedReport(updateSelectedReport))
    }
  }, [filterReport])

  const handleClose = () => {
    setEditModalOpen(false)
    resetForm()
  }
  const resetForm = () => {
    initialFormValues(selectedReport)
  }

  return (
    <Box
      display='grid'
      gridTemplateColumns='repeat(auto-fit, minmax(auto, 15rem))'
      gridTemplateRows='1fr'
      gridAutoFlow='column'
      gap={4}
      boxSizing='border-box'
      data-cy='report-selection-parameters'
    >
      <TextField
        label='Portfolio Name'
        variant='outlined'
        value={initialValue.portfolio}
        size='small'
        aria-readonly={true}
        sx={{ pointerEvents: 'none' }}
        data-cy='portfolio-name'
      />
      <TextField
        label='Benchmark'
        variant='outlined'
        value={initialValue.benchmark}
        size='small'
        aria-readonly={true}
        sx={{ pointerEvents: 'none' }}
        data-cy='benchmark-name'
      />
      <TextField
        label='Template'
        variant='outlined'
        placeholder=''
        value={initialValue.template}
        size='small'
        aria-readonly={true}
        sx={{ pointerEvents: 'none' }}
        data-cy='template-name'
      />
      <TextField
        label={`${selectedReport.reportType === AxiomReportType.GFR || selectedReport.reportType === AxiomReportType.SCEN ? 'As of Date' : 'Period Date Range'}`}
        variant='outlined'
        placeholder=''
        value={initialValue.date}
        size='small'
        aria-readonly={true}
        sx={{ pointerEvents: 'none' }}
        data-cy='selected-report-date'
      />
      <Button
        variant='contained'
        size='small'
        color='primary'
        fullWidth={false}
        sx={{ width: 'fit-content' }}
        onClick={() => setEditModalOpen(true)}
      >
        Edit
      </Button>
      <Modal
        open={editModalOpen}
        onClose={handleClose}
        aria-labelledby='edit-modal'
        aria-describedby='edit-modal'
      >
        <Box
          display='grid'
          alignItems='center'
          justifyContent='center'
          gridTemplateColumns='40rem'
          height='100%'
          sx={{ pointerEvents: 'none' }}
        >
          <Card sx={{ pointerEvents: 'initial' }}>
            {loading && <Loader />}
            <CardHeader
              title={
                <Typography variant='h3'>Edit Report Selection</Typography>
              }
              action={
                <IconButton onClick={handleClose}>
                  <Close fontSize='inherit' />
                </IconButton>
              }
            />
            <CardContent
              sx={{
                borderTop: '1px solid #0000001F',
                borderBottom: '1px solid #0000001F',
              }}
            >
              <Box display='grid' gap={4} px={2}>
                <Stack direction='column' gap={1}>
                  <Typography variant='h5'>1. Select a Portfolio</Typography>
                  <Autocomplete
                    value={portfolio || null}
                    isOptionEqualToValue={(option, value) => option === value}
                    disablePortal
                    options={portfolioList}
                    size='small'
                    onChange={handleChangePortfolio}
                    renderInput={(params) => (
                      <TextField {...params} label='Portfolio Name' />
                    )}
                  />
                </Stack>
                <Stack direction='column' gap={1}>
                  <Typography variant='h5'>2. Select a Benchmark</Typography>
                  <Autocomplete
                    disablePortal
                    value={benchmark || null}
                    isOptionEqualToValue={(option, value) => option === value}
                    options={benchmarkList}
                    size='small'
                    onChange={handleChangeBenchmark}
                    renderInput={(params) => (
                      <TextField {...params} label='Benchmark' />
                    )}
                  />
                </Stack>
                <Stack direction='column' gap={1}>
                  <Typography variant='h5'>3. Select a Template</Typography>
                  <Autocomplete
                    disablePortal
                    value={template || null}
                    isOptionEqualToValue={(option, value) => option === value}
                    options={templateList}
                    size='small'
                    onChange={handleChangeTemplate}
                    renderInput={(params) => (
                      <TextField {...params} label='Template' />
                    )}
                  />
                </Stack>
                <Stack direction='column' gap={1}>
                  <Typography variant='h5'>{`4. Select ${selectedReport.reportType === AxiomReportType.GFR || selectedReport.reportType === AxiomReportType.SCEN ? 'an As of Date' : 'a Date Range'}`}</Typography>
                  <Autocomplete
                    disablePortal
                    value={date || null}
                    isOptionEqualToValue={(option, value) => option === value}
                    options={dateList}
                    size='small'
                    onChange={handleChangeDate}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`${selectedReport.reportType === AxiomReportType.GFR || selectedReport.reportType === AxiomReportType.SCEN ? 'As of Date' : 'Date Range'}`}
                      />
                    )}
                  />
                </Stack>
              </Box>
            </CardContent>
            <CardActions>
              <Stack
                direction='row'
                justifyContent='flex-end'
                width='100%'
                gap={1}
              >
                <Button
                  color='primary'
                  variant='contained'
                  onClick={handleApplyChange}
                  disabled={initialLoad || !isApplyChangeEnabled}
                >
                  Apply Changes
                </Button>
                <Button color='secondary' variant='text' onClick={handleClose}>
                  Cancel
                </Button>
              </Stack>
            </CardActions>
          </Card>
        </Box>
      </Modal>
    </Box>
  )
}
